// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/WorshipAcademy.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiMusic, FiBook, FiUsers, FiAward } from "react-icons/fi";
import Button from "../../components/Common/Button";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";
import { worshipAcademyReadings } from "../../content/worshipAcademyReadings";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.homeText};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing["2xl"]};
`;

const FeatureCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  padding: ${(props) => props.theme.spacing.xl};
  border-radius: 12px;
  text-align: center;
`;

const FeatureIcon = styled.div`
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  color: ${(props) => props.theme.colors.accent};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const FeatureTitle = styled.h4`
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${(props) => props.theme.colors.accent};
    font-size: ${(props) => props.theme.fontSizes.xl};
  }
`;

const MusicTheoryBox = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["2xl"]};
  border-radius: 12px;
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const AcademyReading = styled.section`
  background: ${p => p.theme.colors.homeNavy};
  color: ${p => p.theme.colors.white};
  padding: clamp(20px, 4vw, 40px);
  margin-top: ${p => p.theme.spacing["3xl"]};
  border: 1px solid #24495a;
  border-radius: 8px;
  overflow-wrap: anywhere;
  scroll-margin-top: 100px;
  > div { max-width: 68ch; margin: 0 auto; }
  h2 { font: 700 clamp(1.8rem, 4vw, 2.4rem)/1.2 "DIN Condensed", "Arial Narrow", sans-serif; color: ${p => p.theme.colors.homeGold}; margin-bottom: 24px; }
  h3 { font-size: 1.25rem; line-height: 1.4; color: ${p => p.theme.colors.accent}; margin: 28px 0 14px; }
  p { font-family: ${p => p.theme.fonts.body}; font-size: 1.0625rem; line-height: 1.75; color: #c6d9e2; margin-bottom: 20px; }
  p:last-child { margin-bottom: 0; }
`;

const WorshipAcademy = () => {
  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Music Church Worship Academy - The Music Church</title>
        <meta
          name="description"
          content="Music Church Worship Academy focuses on music education, its design, destiny and purpose. We study and respectfully admire this great art and science as a gift from God."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              Music Church Worship Academy
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              Exploring the divine science of music
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/home-preview/worship-academy-framed-v3.png"
          alt="Music Church Worship Academy"
          maxWidth="800px"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About Worship Academy</SectionTitle>
                <Text>
                  Worship Academy focuses on music education, its design,
                  destiny and purpose. We study and respectfully admire this
                  great art and science, recognizing it as a gift, a thumbprint
                  placed upon each of us, from God, The Master Musician Who
                  created us all... in His image.
                </Text>
                <Text>
                  We recognize the mysterious origin of music, and the harmonic
                  science that defines its structures. The mathematical
                  connections display a magnetism of sound calibrated in a
                  particular order called The overtone series, discovered by
                  Pythagoras in 570 BC.
                </Text>
              </Section>

              <MusicTheoryBox>
                <h3 style={{ marginBottom: "1rem" }}>The Harmonic Universe</h3>
                <p style={{ lineHeight: "1.8" }}>
                  This revelation uncovered a hidden realm of sound that has
                  shaped music for centuries. This invisible spectrum of
                  harmonically related frequencies governs the laws of musical
                  harmony, influencing the creation of scales, chords, and
                  overall musical structure.
                </p>
              </MusicTheoryBox>

              <Section>
                <SectionTitle>The Divine Pattern</SectionTitle>
                <Text>
                  Music is like gravity, the universal language that captures
                  all human emotions. The artistic tapestry woven from 7-note
                  scales and 7 harmonic structures that manifest uniformly
                  across 12 keys, each key resonating with the same familial
                  DNA-like patterns filtered through 7 modes.
                </Text>
                <Text>
                  Much like the diverse cultures of the seven continents—with
                  their unique cuisines, spices, fragrances, fabrics, and
                  languages—these harmonic relationships are intrinsically drawn
                  together, echoing a universal blueprint. This profound
                  significance of seven is mirrored in the natural world, from
                  the 7-day rhythm of our week, to the Earth's revolving dance
                  around the sun.
                </Text>
              </Section>

              <QuoteBox>
                "It is obvious that the artful science of music has been
                designed by a great intelligence. The discovery of these
                'inaudible' tones sparked centuries of scientific study and
                continues to influence all musical traditions today through the
                powerful concept of harmonic magnetism."
              </QuoteBox>

              <Section>
                <SectionTitle>What We Teach</SectionTitle>
                <FeatureGrid>
                  <FeatureCard>
                    <FeatureIcon>
                      <FiMusic />
                    </FeatureIcon>
                    <FeatureTitle>Musical Wisdom</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      Art and science of rythm, melody, and harmony.
                    </p>
                  </FeatureCard>

                  <FeatureCard>
                    <FeatureIcon>
                      <FiMusic />
                    </FeatureIcon>
                    <FeatureTitle>Harmonic Magnetism</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      The study of the natural attraction of foundational
                      harmonics and their influence on musical structures.
                    </p>
                  </FeatureCard>

                  <FeatureCard>
                    <FeatureIcon>
                      <FiBook />
                    </FeatureIcon>
                    <FeatureTitle>Prophetic Worship</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      The scriptural foundation of worship and praise
                    </p>
                  </FeatureCard>

                  <FeatureCard>
                    <FeatureIcon>
                      <FiUsers />
                    </FeatureIcon>
                    <FeatureTitle>As One</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      Creating unity in worship teams and ministries
                    </p>
                  </FeatureCard>

                  <FeatureCard>
                    <FeatureIcon>
                      <FiUsers />
                    </FeatureIcon>
                    <FeatureTitle>Textures and Colors</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      Understanding the art of creating musiocal atmospheres.
                    </p>
                  </FeatureCard>

                  <FeatureCard>
                    <FeatureIcon>
                      <FiUsers />
                    </FeatureIcon>
                    <FeatureTitle>Studio Tech</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      Your guide to hands on practical home studio recording and
                      DAW setup.
                    </p>
                  </FeatureCard>

                  <FeatureCard>
                    <FeatureIcon>
                      <FiUsers />
                    </FeatureIcon>
                    <FeatureTitle>Liking Your Voice</FeatureTitle>
                    <p style={{ fontSize: "0.875rem", color: "#c6d9e2" }}>
                      A practical vocal training program to help you find your
                      unique sound and style.
                    </p>
                  </FeatureCard>
                </FeatureGrid>
              </Section>

              <Section>
                <SectionTitle>Our Mission</SectionTitle>
                <Text>
                  We desire to inspire and educate all musicians and ministry
                  minded organizations, helping them to promote synergy, using
                  this gift to energize the atmosphere, fostering a community of
                  caring and sharing with creative excellence.
                </Text>
                <Text>
                  We want to equip musicians and singers to enhance worship in
                  their own churches, growing and sharing knowledge together
                  with their respective congregations. Paying it forward to the
                  young leaders of the future.
                </Text>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Programs Offered
                </SectionTitle>
                <InfoItem>
                  <FiMusic />
                  <span>Instrumental Training</span>
                </InfoItem>
                <InfoItem>
                  <FiBook />
                  <span>Musical Wisdom Classes</span>
                </InfoItem>
                <InfoItem>
                  <FiUsers />
                  <span>Worship Team Development</span>
                </InfoItem>
                <InfoItem>
                  <FiAward />
                  <span>Certification Programs</span>
                </InfoItem>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  The Sacred Seven
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem", marginBottom: "1rem" }}>
                  The significance of 7 in music and creation:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                  }}
                >
                  <li>7 notes in a scale</li>
                  <li>7 harmonic structures</li>
                  <li>7 modes of music</li>
                  <li>7 days of creation</li>
                  <li>7 continents</li>
                  <li>7 days in a week</li>
                  <li>7 ministries of The Music Church</li>
                </ul>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Quote
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem", fontStyle: "italic" }}>
                  "Where 12 midnight gracefully transforms into 12 noon, and the
                  beat goes on like a never-ending song."
                </Text>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          {worshipAcademyReadings.map(reading => (
            <AcademyReading key={reading.id} id={`academy-${reading.id}`} aria-labelledby={`academy-${reading.id}-title`}>
              <div>
                <h2 id={`academy-${reading.id}-title`}>{reading.title}</h2>
                {reading.blocks.map((block, index) => block.type === "heading"
                  ? <h3 key={index}>{block.text}</h3>
                  : <p key={index}>{block.text}</p>)}
              </div>
            </AcademyReading>
          ))}

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Ready to Explore the Gift of Music?
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Join us in discovering the divine science of music and enhance
              your worship ministry.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="accent"
                size="large"
                onClick={() => console.log("Enroll clicked")}
              >
                Enroll Now
              </Button>
              <Button
                variant="outline"
                size="large"
                style={{ borderColor: "white", color: "white" }}
                onClick={() => console.log("Learn more clicked")}
              >
                Learn More
              </Button>
            </div>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default WorshipAcademy;
